import { useState } from 'react';
import { Modal, RadioButtonGroup, RadioButton } from '@carbon/react';
import { downloadBlob } from 'utils/download-blob';
import { buildAttributeRows, type EconomicsPair } from './property-attributes';
import type { ComparisonResult, EvaluationSummary } from './types';

type ExportScope = 'all' | 'differ';

/** The Origin (a) / Target (b) eval identities, for labeling the export. */
export type EvaluationPair = { a: EvaluationSummary; b: EvaluationSummary };

// The melted comparison CSV header. Origin/Target carry their eval number so a
// downloaded file is self-identifying — which eval is the baseline (Origin) and
// which it is compared against (Target) — matching the on-screen Package Evals
// banner. Falls back to plain Origin/Target when the evals aren't loaded.
const buildHeader = (evaluations: EvaluationPair | null): string[] => [
  'Property ID',
  'Property Name',
  'Attribute',
  evaluations ? `Origin (Eval ${evaluations.a.evaluationId})` : 'Origin',
  evaluations ? `Target (Eval ${evaluations.b.evaluationId})` : 'Target',
  'Variance',
  'Variance %',
];

// Quotes a CSV field, escaping embedded quotes. Also guards against CSV formula
// injection: Excel and Google Sheets treat a cell that begins with = + - @ (or a
// tab / carriage return) as a formula, so a signed Variance like "+$200" imports
// as "#NAME?". Prefixing a tab makes the spreadsheet read the whole field as text
// while preserving the visible value. Exported for unit testing.
export const csvField = (value: string): string => {
  const str = String(value);
  const guarded = /^[=+\-@\t\r]/.test(str) ? `\t${str}` : str;
  return `"${guarded.replace(/"/g, '""')}"`;
};

/**
 * Builds the melted comparison CSV for the given properties.
 * @param {ComparisonResult[]} results - The properties currently shown
 * @param {ExportScope} scope - 'all' attributes or 'differ' (only differing)
 * @param {EconomicsPair | null} economics - Both sides' scenario economics, or null
 * @param {EvaluationPair | null} evaluations - Origin/Target eval identities, for the header labels
 * @return {string} CSV text (header + one row per exported attribute)
 */
export const buildCsv = (
  results: ComparisonResult[],
  scope: ExportScope,
  economics: EconomicsPair | null,
  evaluations: EvaluationPair | null = null
): string => {
  const lines = [buildHeader(evaluations).map(csvField).join(',')];
  results.forEach((result) => {
    const id = result.uwi ?? result.uniqueSlotId;
    const name = result.wellName ?? '';
    const pushRow = (
      attribute: string,
      origin: string,
      target: string,
      variance: string,
      variancePct: string
    ): void => {
      lines.push(
        [id, name, attribute, origin, target, variance, variancePct]
          .map(csvField)
          .join(',')
      );
    };

    buildAttributeRows(result, economics)
      .filter((attr) => scope === 'all' || attr.differs)
      .forEach((attr) => {
        // A multi-value row (Lease Operating Expense Per Well's 5-year series)
        // melts to one CSV row per year, so each year's Origin/Target stays a
        // sortable number with its own Variance / Variance % instead of a single
        // packed cell. The on-screen card/table keep the stacked breakdown.
        if (attr.breakdown) {
          // Under 'differ' scope, drop the sub-lines that match on both sides — the
          // attribute-level `differs` is true if *any* line moved, so without this a
          // changed breakdown would still emit its unchanged lines. Uses each line's
          // numeric `differs` (not a formatted-string compare) so a Property Volume line
          // that moved sub-display — e.g. a fractional MBOE — is kept, matching the on-screen
          // "Differ only" view rather than being silently dropped.
          attr.breakdown
            .filter((line) => scope === 'all' || line.differs)
            .forEach((line) =>
              pushRow(
                `${attr.label} - ${line.tag}`,
                line.aText,
                line.bText,
                line.delta?.abs ?? '',
                line.delta?.pct ?? ''
              )
            );
          return;
        }
        pushRow(
          attr.label,
          attr.aText,
          attr.bText,
          attr.delta?.abs ?? '',
          attr.delta?.pct ?? ''
        );
      });
  });
  return lines.join('\n');
};

// Triggers a client-side download of the CSV text via the shared blob helper.
const downloadCsv = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, filename);
};

interface ExportComparisonModalProps {
  open: boolean;
  onClose: () => void;
  /** The properties currently shown (after search filtering). */
  results: ComparisonResult[];
  /** Both sides' scenario economics, for the per-unit economic rows. */
  economics: EconomicsPair | null;
  /** Origin (a) / Target (b) eval identities, to label the Origin/Target columns. */
  evaluations: EvaluationPair | null;
  /**
   * Prefix for the radio group's name + option ids. Two instances of this modal
   * can be mounted at once (the header "Download comparison" and the Properties
   * toolbar export); a distinct prefix keeps their radios from colliding — Carbon
   * keeps a closed Modal in the DOM, so duplicate names/ids would route label
   * clicks to the wrong (hidden) modal.
   */
  idPrefix?: string;
}

/**
 * "Export comparison" modal (mock v16): choose All attributes vs Only attributes
 * that differ, then download a melted CSV of the properties currently shown.
 * @param {ExportComparisonModalProps} props - open state, close handler, results
 * @return {JSX.Element} The export modal
 */
export const ExportComparisonModal = ({
  open,
  onClose,
  results,
  economics,
  evaluations,
  idPrefix = 'export',
}: ExportComparisonModalProps) => {
  const [scope, setScope] = useState<ExportScope>('all');

  const handleExport = () => {
    downloadCsv(
      buildCsv(results, scope, economics, evaluations),
      'package-comparison.csv'
    );
    onClose();
  };

  return (
    <Modal
      open={open}
      modalHeading="Export comparison"
      primaryButtonText="Export"
      secondaryButtonText="Cancel"
      onRequestClose={onClose}
      onRequestSubmit={handleExport}
      size="sm"
    >
      <p>
        Choose how much of the comparison to include. Both options cover the
        properties currently shown.
      </p>
      <RadioButtonGroup
        legendText="Include"
        name={`${idPrefix}-scope`}
        orientation="vertical"
        valueSelected={scope}
        onChange={(value) => setScope(value as ExportScope)}
      >
        <RadioButton
          labelText="All attributes"
          value="all"
          id={`${idPrefix}-all`}
        />
        <RadioButton
          labelText="Only attributes that differ"
          value="differ"
          id={`${idPrefix}-differ`}
        />
      </RadioButtonGroup>
    </Modal>
  );
};
